// src/pages/PasswordChangePage.js
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageWrapper from "../pageWrapper";

// CSS
import "./VerificationPage.css";

// Components
import PageLoader from "Components/Other Components/pageLoader";

import Button from "../../Components/Other Components/button";
import Title from "../../Components/Other Components/Title";
import InputField from "Components/Other Components/inputField";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";

// Functions and hooks
import {
  VerifyPasswordResetByToken,
  ResetPassword,
  Logout,
} from "../../BackendAccess/UserHooks";
import { checkPasswordInput } from "../../BackendAccess/FrontendHelpers";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useNotification } from "../../Contexts/NotificationContext";

export default function PasswordChangePage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  useEffect(() => {
    verifyToken();
  }, []);

  const verifyToken = async () => {
    try {
      const [data, err] = await fetchWithTimeout(() =>
        VerifyPasswordResetByToken(token)
      );
      if (err) {
        navigate(-1);
        err.isPageError = true;
        setError(err);
      }
    } catch {
      navigate(-1);
      setError({ err: t("passwordChange.error.server"), errCode: 500 });
    }
  };

  const applyChange = async () => {
    if (newPassword !== newPasswordConfirm) {
      showNotification(t("passwordChange.error.passwordMismatch"), "error");
      return;
    }
    const passError = checkPasswordInput(newPassword);
    if (passError !== true) {
      setError(passError);
      return;
    }
    try {
      showNotification(t("passwordChange.info.resetting"), "info");
      const [data, err] = await fetchWithTimeout(() =>
        ResetPassword(token, newPasswordConfirm)
      );
      if (err) {
        err.isPageError = true;
        setError(err);
      } else {
        showNotification(t("passwordChange.success.changed"), "success");
        await logoutUser();
        navigate("/login");
      }
    } catch {
      setError({ err: t("passwordChange.error.server"), errCode: 500 });
    }
  };

  const logoutUser = async () => {
    try {
      const [data, err] = await fetchWithTimeout(Logout);
      if (err) {
        err.isPageError = true;
        setError(err);
      } else {
        showNotification(t("passwordChange.info.loggedOut"), "info");
      }
    } catch {
      setError({ err: t("passwordChange.error.server"), errCode: 500 });
    }
  };

  return (
    <PageWrapper>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <div className="container-bordered verification-container mt-3">
            <Title>{t("passwordChange.title")}</Title>

            <InputField
              label={t("passwordChange.label.new")}
              type="password"
              labelPosition="top-left"
              value={newPassword}
              onChange={setNewPassword}
              mandatory
            />
            <InputField
              label={t("passwordChange.label.confirm")}
              type="password"
              labelPosition="top-left"
              value={newPasswordConfirm}
              onChange={setNewPasswordConfirm}
              mandatory
            />
            <MandatoryExplanation />

            <div className="btn-container mt-3 w-100">
              <Button onClick={applyChange} btnStyle="btn-positive">
                {t("passwordChange.button.apply")}
              </Button>
            </div>
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
